import { IntegrationSourceType } from '@/client/services/api/graphql/gql/graphql';
import { Flex, Progress, Stack, Text } from '@chakra-ui/react';

import type { ExpertFormData } from '../../types';
import ExpertIntegrationContainer from '../ExpertIntegrationContainer';
import { FolderUpload } from '@/client/components/icons/ContinuIcons';
import LocalUploadFileList from './LocalUploadFileList';
import LocalUploadFileListModal from './LocalUploadFileListModal';
import MediaUploadDropzone from '@/client/components/shared/media/uploads/MediaUploadDropzone';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import S3Service from '@/client/services/api/aws/S3Service';
import { useAuthStore } from '@/client/services/state/authStore';
import { useExpertsStore } from '../../../expertsStore';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { getExtensionTypeFromFile } from '@/client/utils/type-maps/extensionTypeFromFile';

// TODO: We'll need to update the error logic here as integrations are added because local upload will not be required if resources from an integration exist.


export default function ExpertLocalUploadIntegrationForm() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { isEditingExpert: isEditing } = useExpertsStore();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { _id: userId } = user;
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<ExpertFormData>();
  const { localUploadIntegration, resources } = watch();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [fileListModalIsOpen, setFileListModalIsOpen] = useState(false);

  const LOCAL_RESOURCE_FILE_LIMIT = 20;

  const handleFileInput = useMutation({
    mutationFn: (file: File) => {
      setCurrentFile(file);

      return S3Service.uploadToBucket(file, (progress) => setUploadProgress(progress));
    },
    onSuccess: async (successData, file) => {
      const newResources = [
        ...(resources || []),
        {
          addedBy: userId,
          downloadLink: successData.cloudFrontLink,
          extensionName: file.name,
          extensionType: getExtensionTypeFromFile(file),
          id: '',
          path: successData.cloudFrontLink,
          source: IntegrationSourceType.Upload,
          sourceId: file.name,
          isFolder: false,
        },
      ];

      setValue('resources', newResources, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });

      setUploadProgress(0);
      setCurrentFile(null);

      setToast({
        show: true,
        status: 'success',
        title: t('admin.aiAgent.experts.form.integrations.localUpload.uploadSuccess'),
      });

      // Use setTimeout to ensure the state update has propagated before triggering validation
      setTimeout(async () => {
        await trigger();
      }, 0);
    },
    onError: () => {
      // TODO: Add error handling/toast notification
      setUploadProgress(0);
      setCurrentFile(null);

      setToast({
        show: true,
        status: 'error',
        title: t('admin.aiAgent.experts.form.integrations.localUpload.uploadError'),
      });
    },
  });

  const { status } = handleFileInput;

  const acceptedFileTypes = ['.txt', '.md', '.doc', '.docx', '.csv', '.xls', '.xlsx', '.pdf'];

  const acceptedMimeTypes = [
    'text/plain', // .txt
    'text/markdown', // .md
    'text/x-markdown', // .md (alternative)
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/csv', // .csv
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/pdf', // .pdf
  ];

  const uploadResources = (allResources: ExpertFormData['resources'] = []) =>
    allResources.filter((resource) => resource.source === IntegrationSourceType.Upload);

  const hasError =
    !!errors.resources &&
    uploadResources(resources).length === 0;
  return (
    <ExpertIntegrationContainer
      renderSetupButton={false}
      integrationEnabled={localUploadIntegration}
      handleOnSetupButtonClick={() => {}}
      integrationName={t('admin.aiAgent.experts.form.integrations.localUpload.title')}
      integrationDescription={t('admin.aiAgent.experts.form.integrations.localUpload.helpText')}
      integrationIcon={<FolderUpload color="#627FFF" boxSize={8} />}
      hasError={hasError}
      errorMessage={errors.resources?.message as string}
    >
      <Stack paddingTop={8} spacing={8}>
        {uploadResources(resources).length > 0 && <LocalUploadFileList renderLimit={5} />}

        {uploadResources(resources).length > 5 && (
          <OutlineButton width="fit-content" onClick={() => setFileListModalIsOpen(true)}>
            {t('admin.aiAgent.experts.form.integrations.localUpload.viewAll', {
              count: uploadResources(resources).length,
            })}
          </OutlineButton>
        )}

        {isEditing && (
          <>
            {status === 'loading' && currentFile && (
              <Stack>
                <Flex justifyContent="space-between" alignItems="center">
                  <Text fontSize="14px" fontWeight={500} color="baseBlack">
                    {currentFile.name}
                  </Text>

                  <Text fontSize="14px" fontWeight={500} color="baseBlack">
                    {uploadProgress}%
                  </Text>
                </Flex>

                <Progress size="sm" value={uploadProgress} width="full" />
              </Stack>
            )}

            <MediaUploadDropzone
              isDisabled={status === 'loading' || resources.length >= LOCAL_RESOURCE_FILE_LIMIT}
              acceptedFileTypes={acceptedFileTypes}
              acceptedMimeTypes={acceptedMimeTypes}
              onFileInput={(file: File) => handleFileInput.mutateAsync(file)}
              onSuccessfulDrop={(file: File) => handleFileInput.mutateAsync(file)}
              dropzoneContent={
                resources.length >= LOCAL_RESOURCE_FILE_LIMIT ? (
                  <Text fontSize="12px" fontWeight={400} color="neutral.700">
                    {t('admin.aiAgent.experts.form.integrations.localUpload.storageLimitReached')}
                  </Text>
                ) : undefined
              }
            />
          </>
        )}
      </Stack>

      <LocalUploadFileListModal
        isOpen={fileListModalIsOpen}
        onClose={() => setFileListModalIsOpen(false)}
      />
    </ExpertIntegrationContainer>
  );
}
